import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Check, Copy, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useWorkspace } from '@/contexts/workspace-context';
import { formatInstagramHandle } from '@/lib/channel-display';
import { workspaceUrl } from '@/lib/workspace-url';
import type { FacebookChannel } from '@/types/channel';
import { ChannelCommonRows, type ChannelCommonRowsProps } from './channel-common-rows';

export interface FacebookChannelBodyProps extends Omit<ChannelCommonRowsProps, 'channelId'> {
  // Narrowed to FacebookChannel; callers guard with channel.type === 'facebook'.
  channel: FacebookChannel;
}

/**
 * AIT-621 Facebook Page channel body: the shared rows plus the Page id, Page
 * name and a link to the instagram channel connected through this Page (an
 * instagram row whose metaPageId is this Page). The messaging-window panel
 * arrives with plan 05 together with its API field.
 */
export function FacebookChannelBody({ channel, ...common }: FacebookChannelBodyProps) {
  const { channels } = useWorkspace();
  const { ws } = useParams();
  const [copiedPageId, setCopiedPageId] = useState(false);
  // A Page whose linked account changed keeps the old row behind, disconnected.
  const linkedInstagram = channels.find(
    (c) => c.type === 'instagram' && c.metaPageId === channel.metaResourceId && c.channelStatus === 'connected',
  );

  async function copyPageId() {
    try {
      await navigator.clipboard.writeText(channel.metaResourceId);
      setCopiedPageId(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedPageId(false), 2000);
    } catch {
      toast.error('Failed to copy. Please copy manually.');
    }
  }

  return (
    <div className="mt-2 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-x-10 gap-y-4 text-sm">
      <ChannelCommonRows channelId={channel.id} {...common} />

      <span className="flex items-center gap-1 text-muted-foreground">
        <span>Page ID</span>
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="What is a Page ID?"
                className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
              />
            }
          >
            <Info className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            Your Facebook Page ID at Meta. Use it to find this Page in Meta Business Suite.
          </TooltipContent>
        </Tooltip>
      </span>
      <span className="flex items-center gap-1">
        <span>{channel.metaResourceId}</span>
        <Button variant="ghost" size="icon-xs" onClick={copyPageId} aria-label="Copy Page ID" title="Copy Page ID">
          {copiedPageId ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </Button>
      </span>

      <span className="text-muted-foreground">Page Name</span>
      <span>{channel.facebookPageName ?? 'N/A'}</span>

      <span className="text-muted-foreground">Linked Instagram</span>
      <span>
        {linkedInstagram && linkedInstagram.type === 'instagram' && ws ? (
          <Link to={workspaceUrl(ws, `channels/${linkedInstagram.id}`)} className="underline-offset-4 hover:underline">
            {linkedInstagram.instagramUsername
              ? formatInstagramHandle(linkedInstagram.instagramUsername)
              : 'Instagram account'}
          </Link>
        ) : (
          'None'
        )}
      </span>
    </div>
  );
}
